import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import type { ReactNode } from 'react'
import {
  LBBadge,
  LBTooltip,
  LBTooltipTrigger,
  LBTooltipContent
} from '@linearb/buzz'
import useTimeFormat from '../hooks/useTimeFormat'

dayjs.extend(relativeTime)
dayjs.extend(utc)

// Backend timestamps are UTC without a tz suffix; parse as UTC then show in local time.
export const parseUtc = (dateStr?: string | null) => dayjs.utc(dateStr).local()

export const getTimeFormatToken = (
  use24Hour: boolean,
  withSeconds = false
): string => {
  if (use24Hour) {
    return withSeconds ? 'HH:mm:ss' : 'HH:mm'
  }
  return withSeconds ? 'h:mm:ss A' : 'h:mm A'
}

export const getDateTimeFormat = (
  use24Hour: boolean,
  datePattern = 'MMM D, YYYY',
  withSeconds = false
): string => `${datePattern} ${getTimeFormatToken(use24Hour, withSeconds)}`

export const formatDateTime = (
  date: dayjs.Dayjs,
  use24Hour: boolean,
  datePattern = 'MMM D, YYYY',
  withSeconds = false
): string => date.format(getDateTimeFormat(use24Hour, datePattern, withSeconds))

// Inclusive day span between two YYYY-MM-DD dates (both ends counted).
export const inclusiveDays = (since: string, until: string): number =>
  dayjs(until).diff(dayjs(since), 'day') + 1

export const firstOfMonthMs = () => dayjs().startOf('month').valueOf()

const PR_NUMBER_RE = /\/pull-requests\/(\d+)$|\/pull\/(\d+)$/

export const extractPrNumber = (url: string | null | undefined): string => {
  if (!url) {
    return ''
  }
  const match = url.match(PR_NUMBER_RE)
  return match ? match[1] || match[2] : ''
}

export const DASH_FALLBACK = <span className='text-muted-foreground'>—</span>

interface RelativeDateTooltipProps {
  dateStr?: string | null
  dateOnly?: boolean
  className?: string
  fallback?: ReactNode
  prefix?: string
  tooltipDatePattern?: string
}

/**
 * Relative time in plain text with the exact date (and time, respecting the
 * 24h preference) on hover.
 * `dateOnly` is for calendar dates that carry no real time-of-day: parsed with raw
 * dayjs so they never shift across midnight in negative-offset zones.
 */
export const RelativeDateTooltip = ({
  dateStr,
  dateOnly = false,
  className = 'cursor-help',
  fallback = null,
  prefix = '',
  tooltipDatePattern = 'MMM D, YYYY'
}: RelativeDateTooltipProps) => {
  const { use24Hour } = useTimeFormat()
  if (!dateStr) {
    return <>{fallback}</>
  }
  const date = dateOnly ? dayjs(dateStr) : parseUtc(dateStr)
  if (!date.isValid()) {
    return <>{fallback}</>
  }
  // Calendar dates compare day-to-day: measuring a midnight value against "now"
  // rounds by time of day, so Aug 9 would read "5 days ago" on Aug 13.
  const relativeText = () => {
    if (!dateOnly) {
      return date.fromNow()
    }
    const today = dayjs().startOf('day')
    const day = date.startOf('day')
    const days = today.diff(day, 'day')
    if (days === 0) {
      return 'Today'
    }
    if (days === 1) {
      return 'Yesterday'
    }
    return day.from(today)
  }
  const relative = relativeText()
  const tooltipFormat = dateOnly
    ? 'MMM D, YYYY'
    : getDateTimeFormat(use24Hour, tooltipDatePattern)
  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <span className={className}>
          {prefix}
          {relative}
        </span>
      </LBTooltipTrigger>
      <LBTooltipContent>{date.format(tooltipFormat)}</LBTooltipContent>
    </LBTooltip>
  )
}

/**
 * Format a date string as "MMM D, YYYY" with a tooltip showing relative time.
 * Returns null if the date is invalid or empty.
 */
export const formatDateWithRelative = dateStr => {
  if (!dateStr) {
    return null
  }
  const date = parseUtc(dateStr)
  if (!date.isValid()) {
    return null
  }
  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <span>{date.format('MMM D, YYYY')}</span>
      </LBTooltipTrigger>
      <LBTooltipContent>{date.fromNow()}</LBTooltipContent>
    </LBTooltip>
  )
}

// null = no/invalid date; otherwise 'expired' | 'soon' | 'ok'
export const getExpiryStatus = dateStr => {
  if (!dateStr) {
    return null
  }
  const date = parseUtc(dateStr)
  if (!date.isValid()) {
    return null
  }
  if (date.isBefore(dayjs())) {
    return 'expired'
  }
  if (date.diff(dayjs(), 'day') <= 30) {
    return 'soon'
  }
  return 'ok'
}

export const ExpiryBadge = ({ expiry }) => (
  <>
    {expiry === 'expired' && (
      <LBBadge variant='destructive' className='text-xs px-2 py-0.5 ml-2'>
        Expired
      </LBBadge>
    )}
    {expiry === 'soon' && (
      <LBBadge
        variant='default'
        className='bg-yellow-500 hover:bg-yellow-500 text-xs px-2 py-0.5 ml-2'
      >
        Expires soon
      </LBBadge>
    )}
  </>
)
